/**
 * TagInput.tsx - Tag input with autocomplete
 * 
 * CHANGE HISTORY:
 * - 251102_0627 [pending] danielbeer: Initial creation
 */

import React, { useState, useRef, useEffect } from 'react';
import type { Tag } from '../types';

export interface TagInputProps {
  tags: string[];
  onChange: (tags: string[]) => void;
  suggestions?: Tag[];
  allowCreate?: boolean;
  placeholder?: string;
  maxTags?: number;
  disabled?: boolean;
  className?: string;
}

export const TagInput: React.FC<TagInputProps> = ({
  tags,
  onChange,
  suggestions = [],
  allowCreate = true,
  placeholder = 'Add tags...',
  maxTags,
  disabled = false,
  className = ''
}) => {
  const [input, setInput] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  // Filter suggestions based on input
  const filteredSuggestions = suggestions.filter(tag =>
    tag.name.toLowerCase().includes(input.toLowerCase()) &&
    !tags.includes(tag.name)
  );

  // Add tag
  const addTag = (tagName: string) => {
    const trimmed = tagName.trim();
    if (!trimmed) return;
    if (tags.includes(trimmed)) return;
    if (maxTags && tags.length >= maxTags) return;

    onChange([...tags, trimmed]);
    setInput('');
    setShowSuggestions(false);
    setSelectedIndex(0);
  };

  // Remove tag
  const removeTag = (index: number) => {
    onChange(tags.filter((_, i) => i !== index));
  };

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (showSuggestions && filteredSuggestions.length > 0) {
        addTag(filteredSuggestions[selectedIndex].name);
      } else if (allowCreate && input.trim()) {
        addTag(input);
      }
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
      setSelectedIndex(0);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(prev =>
        prev < filteredSuggestions.length - 1 ? prev + 1 : prev
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev => (prev > 0 ? prev - 1 : 0));
    } else if (e.key === 'Backspace' && !input && tags.length > 0) {
      removeTag(tags.length - 1);
    } else if (e.key === ',') {
      e.preventDefault();
      if (input.trim()) {
        addTag(input);
      }
    }
  };

  // Handle input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInput(value);
    setShowSuggestions(value.length > 0);
    setSelectedIndex(0);
  };

  // Click outside to close suggestions
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (inputRef.current && !inputRef.current.contains(e.target as Node)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className={`card ${className}`} ref={inputRef} style={{ position: 'relative' }}>
      <div className="card-body" style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', alignItems: 'center', padding: '0.5rem' }}>
        {/* Existing tags */}
        {tags.map((tag, index) => (
          <span key={index} className="badge badge-secondary" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}>
            {tag}
            <button
              type="button"
              onClick={() => removeTag(index)}
              className="icon-btn"
              disabled={disabled}
              aria-label={`Remove ${tag}`}
              style={{ padding: '0', width: '16px', height: '16px', fontSize: '14px', lineHeight: '1' }}
            >
              ×
            </button>
          </span>
        ))}

        {/* Input field */}
        <input
          type="text"
          value={input}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => input && setShowSuggestions(true)}
          placeholder={tags.length === 0 ? placeholder : ''}
          disabled={disabled || (maxTags !== undefined && tags.length >= maxTags)}
          className="input-field"
          aria-label="Tag input"
          aria-autocomplete="list"
          aria-expanded={showSuggestions}
          style={{ flex: '1', minWidth: '120px', border: 'none', padding: '0.25rem 0.5rem' }}
        />
      </div>

      {/* Suggestions dropdown */}
      {showSuggestions && filteredSuggestions.length > 0 && (
        <div className="card" style={{ position: 'absolute', top: '100%', left: 0, right: 0, marginTop: '0.25rem', zIndex: 1000, maxHeight: '200px', overflow: 'auto' }}>
          <ul role="listbox" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {filteredSuggestions.map((suggestion, index) => (
              <li
                key={suggestion.id}
                className={`list-item ${index === selectedIndex ? 'active' : ''}`}
                onClick={() => addTag(suggestion.name)}
                onMouseEnter={() => setSelectedIndex(index)}
                role="option"
                aria-selected={index === selectedIndex}
                style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '0.5rem',
                  padding: '0.5rem',
                  cursor: 'pointer',
                  background: index === selectedIndex ? 'var(--interactive-selected)' : 'transparent'
                }}
              >
                <span
                  style={{ 
                    width: '12px', 
                    height: '12px', 
                    borderRadius: '50%', 
                    backgroundColor: suggestion.color,
                    flexShrink: 0
                  }}
                />
                <span style={{ fontWeight: 500 }}>{suggestion.displayName}</span>
                {suggestion.description && (
                  <span style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>{suggestion.description}</span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Create new tag hint */}
      {showSuggestions && allowCreate && input.trim() && filteredSuggestions.length === 0 && (
        <div className="card" style={{ position: 'absolute', top: '100%', left: 0, right: 0, marginTop: '0.25rem', zIndex: 1000 }}>
          <div className="card-body" style={{ padding: '0.5rem', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
            Press Enter to create "{input}"
          </div>
        </div>
      )}
    </div>
  );
};
